import { CalendarDays, Megaphone, Pencil } from "lucide-react";
import {
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import {
  ExternalAnchor,
  PillGroup,
  SectionLabel,
} from "@/components/market-intel/components";
import { formatShortDate } from "@/components/market-intel/dates";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import { hostOf, riseStyle } from "@/components/market-intel/util";

import { ActivityForm } from "./ActivityForm";
import { activityKindLabelKey, filterActivity } from "./intel";
import { PlatformBadge } from "./PlatformIcon";
import {
  matchesPlatformFilter,
  PLATFORM_FILTERS,
  platformFilterLabelKey,
  type PlatformFilter,
} from "./platforms";
import type { CompetitorMarketingActivity } from "./types";
import {
  AddButton,
  ConfirmDeleteButton,
  IconAction,
  SectionCard,
  SoftChip,
} from "./ui";

/** The competitor's ads, posts, videos and campaigns, newest first. */
export const ActivitySection = ({
  id,
  competitorId,
}: {
  id: string;
  competitorId: Identifier;
}) => {
  const translate = useTranslate();
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [editing, setEditing] = useState<
    CompetitorMarketingActivity | "new" | null
  >(null);
  const { data } = useGetList<CompetitorMarketingActivity>(
    "competitor_marketing_activity",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "published_at", order: "DESC" },
      filter: { competitor_id: competitorId },
    },
  );
  const all = data ?? [];
  const visible = filterActivity(all, platform);
  // Only the networks this competitor has activity on (plus "Todas").
  const filters = PLATFORM_FILTERS.filter(
    (filter) =>
      filter === "all" ||
      all.some((item) => matchesPlatformFilter(item.platform, filter)),
  );

  return (
    <SectionCard
      id={id}
      icon={Megaphone}
      title={translate("competition.activity.heading")}
      count={all.length || undefined}
      style={riseStyle(3)}
      action={
        <AddButton
          label={translate("competition.activity.add")}
          onClick={() => setEditing("new")}
        />
      }
    >
      {filters.length > 2 ? (
        <div className="mb-3">
          <PillGroup<PlatformFilter>
            label={translate("competition.platform_filter.label")}
            value={platform}
            options={filters.map((value) => ({
              value,
              label: translate(platformFilterLabelKey(value)),
            }))}
            onChange={setPlatform}
          />
        </div>
      ) : null}
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate(
            all.length
              ? "competition.activity.empty_filtered"
              : "competition.activity.empty",
          )}
        </p>
      ) : (
        <ol className="-mx-1 divide-y divide-border/70">
          {visible.map((item) => (
            <ActivityRow
              key={item.id}
              item={item}
              onEdit={() => setEditing(item)}
            />
          ))}
        </ol>
      )}
      <ActivityForm
        open={editing != null}
        onOpenChange={(open) => !open && setEditing(null)}
        competitorId={competitorId}
        item={editing === "new" ? null : editing}
      />
    </SectionCard>
  );
};

const ActivityRow = ({
  item,
  onEdit,
}: {
  item: CompetitorMarketingActivity;
  onEdit: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove, { isPending }] = useDelete<CompetitorMarketingActivity>();
  return (
    <li
      className="min-w-0 px-1 py-3 first:pt-0 last:pb-0"
      data-kind={item.kind}
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <PlatformBadge platform={item.platform} size="sm" className="mt-0.5" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 items-center gap-x-2">
            <SoftChip>{translate(activityKindLabelKey(item.kind))}</SoftChip>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
              <CalendarDays aria-hidden className="size-3.5" />
              {item.published_at ? (
                <time dateTime={item.published_at}>
                  {formatShortDate(item.published_at, locale)}
                </time>
              ) : (
                translate("competition.activity.undated")
              )}
            </span>
            <div className="-my-1.5 -mr-1 ml-auto flex shrink-0">
              <IconAction
                label={`${translate("competition.activity.edit")}: ${item.title}`}
                icon={Pencil}
                onClick={onEdit}
              />
              <ConfirmDeleteButton
                label={`${translate("competition.form.delete")}: ${item.title}`}
                disabled={isPending}
                onConfirm={() =>
                  remove(
                    "competitor_marketing_activity",
                    { id: item.id, previousData: item },
                    {
                      mutationMode: "optimistic",
                      onSuccess: () =>
                        notify("competition.form.deleted", { type: "info" }),
                      onError: () =>
                        notify("competition.form.error", { type: "error" }),
                    },
                  )
                }
              />
            </div>
          </div>
          <h3 className="text-[15px] leading-snug font-semibold wrap-anywhere">
            {item.title}
          </h3>
          {item.description ? (
            <MiniMarkdown source={item.description} className="text-sm" />
          ) : null}
          {item.notes ? (
            <div className="rounded-xl bg-amber-500/8 px-2.5 py-1.5 ring-1 ring-amber-500/15 ring-inset dark:bg-amber-400/8 dark:ring-amber-400/15">
              <SectionLabel>
                {translate("competition.activity.notes")}
              </SectionLabel>
              <MiniMarkdown source={item.notes} className="text-sm" />
            </div>
          ) : null}
          {item.url ? (
            <ExternalAnchor href={item.url} className="text-sm">
              {translate("competition.activity.open")} · {hostOf(item.url)}
            </ExternalAnchor>
          ) : null}
        </div>
      </div>
    </li>
  );
};
